import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabaseClient';

const ToppersWall = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [toppers, setToppers] = useState({
    daily: [],
    weekly: [],
    monthly: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('daily');

  useEffect(() => {
    const fetchToppers = async () => {
      try {
        const { data: allToppers, error } = await supabase
          .from('toppers')
          .select('*')
          .order('name', { ascending: true }); // Sorted alphabetically on the basis of name

        if (error) throw error;

        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
        const oneWeekAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
        const oneMonthAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

        const dailyToppers = allToppers.filter(topper => {
          const topperDate = new Date(topper.lastUpdated);
          return topperDate >= oneDayAgo;
        });

        const weeklyToppers = allToppers.filter(topper => {
          const topperDate = new Date(topper.lastUpdated);
          return topperDate >= oneWeekAgo;
        });

        const monthlyToppers = allToppers.filter(topper => {
          const topperDate = new Date(topper.lastUpdated);
          return topperDate >= oneMonthAgo;
        });

        setToppers({
          daily: dailyToppers,
          weekly: weeklyToppers,
          monthly: monthlyToppers
        });
        setLoading(false);
      } catch (err) {
        console.error("Error fetching toppers: ", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchToppers();
  }, []);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading Excellence Hall of Fame...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>Connection Issue: {error}</p>
        <p style={styles.errorSubtext}>Please check your connection and try again</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <h1 style={styles.mainTitle}>Academic Excellence</h1>
          <div style={styles.titleUnderline}></div>
        </div>
        <p style={styles.subtitle}>Celebrating outstanding achievements and academic success</p>
      </div>

      <div style={styles.tabContainer}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'daily' ? styles.activeTab : {}) }}
          onClick={() => setActiveTab('daily')}
        >
          <span style={styles.tabIcon}>Today</span>
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'weekly' ? styles.activeTab : {}) }}
          onClick={() => setActiveTab('weekly')}
        >
          <span style={styles.tabIcon}>Week</span>
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'monthly' ? styles.activeTab : {}) }}
          onClick={() => setActiveTab('monthly')}
        >
          <span style={styles.tabIcon}>Month</span>
        </button>
      </div>

      <div style={styles.content}>
        {activeTab === 'daily' && (
          <TopperSection
            toppers={toppers.daily}
            title="Today's Top Performers"
            period="daily"
          />
        )}
        {activeTab === 'weekly' && (
          <TopperSection
            toppers={toppers.weekly}
            title="This Week's Achievers"
            period="weekly"
          />
        )}
        {activeTab === 'monthly' && (
          <TopperSection
            toppers={toppers.monthly}
            title="Monthly Excellence"
            period="monthly"
          />
        )}
      </div>
    </div>
  );
};

const TopperSection = ({ toppers, title, period }) => {
  return (
    <section style={styles.section}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <div style={styles.sectionCount}>
          {toppers.length} {toppers.length === 1 ? 'Student' : 'Students'}
        </div>
      </div>
      <TopperList toppers={toppers} period={period} />
    </section>
  );
};

const TopperList = ({ toppers, period }) => {
  if (toppers.length === 0) {
    return (
      <div style={styles.emptyState}>
        <p style={styles.emptyText}>No records yet</p>
        <p style={styles.emptySubtext}>Be the first to make your mark!</p>
      </div>
    );
  }

  return (
    <div style={styles.list}>
      {toppers.map((topper, index) => (
        <TopperCard key={topper.id} topper={topper} rank={index + 1} period={period} />
      ))}
    </div>
  );
};

const TopperCard = ({ topper, rank, period }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getRankStyle = (rank) => {
    if (rank === 1) return styles.goldRank;
    if (rank === 2) return styles.silverRank;
    if (rank === 3) return styles.bronzeRank;
    return styles.standardRank;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return { labelText: '1st', label: 'First Place' };
    if (rank === 2) return { labelText: '2nd', label: 'Second Place' };
    if (rank === 3) return { labelText: '3rd', label: 'Third Place' };
    return { labelText: `${rank}th`, label: `Rank ${rank}` };
  };

  const rankInfo = getRankIcon(rank);
  const accuracy = topper.totalQuestions ? Math.round((topper.score / topper.totalQuestions) * 100) : 0;

  return (
    <div
      style={{
        ...styles.card,
        ...getRankStyle(rank),
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 12px 24px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.03)',
        borderColor: isHovered ? '#3182ce' : '#e2e8f0'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={styles.cardHeader}>
        <div style={styles.rankContainer}>
          <div style={styles.rankBadge}>
            <span style={styles.rankText} aria-label={rankInfo.label}>
              {rankInfo.labelText}
            </span>
          </div>
        </div>

        <div style={styles.accuracyContainer}>
          <span style={styles.accuracyValue}>{accuracy}%</span>
          <span style={styles.accuracyLabel}>accuracy</span>
        </div>
      </div>

      <div style={styles.cardBody}>
        <h3 style={styles.name}>{topper.name || "Student"}</h3>
        <p style={styles.class}>Class {topper.class || "X"}</p>

        <div style={styles.scoreDisplay}>
          <div style={styles.scoreText}>
            <span style={styles.scoreValue}>{topper.score || 0}</span>
            <span style={styles.scoreDivider}>/</span>
            <span style={styles.totalQuestions}>{topper.totalQuestions || 10}</span>
          </div>
        </div>

        <div style={styles.topicContainer}>
          <span style={styles.topicValue}>{topper.topic || "General Knowledge"}</span>
        </div>

        <div style={styles.attemptsContainer}>
          <span style={styles.attemptsText}>{topper.totalAttempts || 1} attempt{topper.totalAttempts !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div style={styles.cardFooter}>
        <div style={styles.periodBadge}>
          {period === 'daily' ? 'Today' : period === 'weekly' ? 'This Week' : 'This Month'}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: '0 24px 100px',
    fontFamily: "'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    maxWidth: '1100px',
    margin: '90px auto 0',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  titleContainer: {
    position: 'relative',
    display: 'inline-block',
    marginBottom: '12px',
  },
  mainTitle: {
    fontSize: '2.4rem',
    fontWeight: '800',
    color: '#1e293b',
    margin: '0 0 12px 0',
    letterSpacing: '-0.5px',
  },
  titleUnderline: {
    height: '4px',
    width: '80px',
    background: 'linear-gradient(135deg, #4f46e5, #3b82f6)',
    margin: '0 auto',
    borderRadius: '10px',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: '#64748b',
    margin: '0',
    fontWeight: '500',
    maxWidth: '500px',
    margin: '0 auto',
    lineHeight: '1.5',
  },
  tabContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '40px',
    gap: '12px',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '10px 24px',
    border: '1px solid #e2e8f0',
    borderRadius: '30px',
    background: 'white',
    color: '#64748b',
    fontWeight: '700',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)',
    minWidth: '100px',
  },
  activeTab: {
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
  },
  tabIcon: {
    fontSize: '14px',
  },
  content: {
    marginBottom: '32px',
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.02)',
    border: '1px solid #edf2f7',
    marginBottom: '24px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '32px',
    paddingBottom: '20px',
    borderBottom: '1px solid #f1f5f9',
    flexWrap: 'wrap',
    gap: '12px',
  },
  sectionTitle: {
    fontSize: '1.4rem',
    fontWeight: '800',
    color: '#1e293b',
    margin: '0',
    letterSpacing: '-0.3px',
  },
  sectionCount: {
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    color: '#3b82f6',
    fontSize: '14px',
    fontWeight: '700',
    padding: '6px 16px',
    borderRadius: '20px',
  },
  list: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '24px',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
    border: '1px solid #e2e8f0',
    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  goldRank: {
    borderTop: '4px solid #d69e2e',
  },
  silverRank: {
    borderTop: '4px solid #a0aec0',
  },
  bronzeRank: {
    borderTop: '4px solid #ed8936',
  },
  standardRank: {
    borderTop: '4px solid #edf2f7',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  rankContainer: {
    position: 'relative',
  },
  rankBadge: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    padding: '6px 14px',
    borderRadius: '20px',
    display: 'inline-block',
  },
  rankText: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#1e293b',
  },
  accuracyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  accuracyValue: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#10b981',
  },
  accuracyLabel: {
    fontSize: '11px',
    color: '#94a3b8',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cardBody: {
    marginBottom: '20px',
    textAlign: 'center',
    flexGrow: 1,
  },
  name: {
    fontSize: '1.2rem',
    fontWeight: '800',
    color: '#1e293b',
    margin: '0 0 6px 0',
    letterSpacing: '-0.3px',
  },
  class: {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '600',
    margin: '0 0 20px 0',
  },
  scoreDisplay: {
    marginBottom: '20px',
  },
  scoreText: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'baseline',
    gap: '4px',
  },
  scoreValue: {
    fontSize: '28px',
    fontWeight: '800',
    color: '#1e293b',
  },
  scoreDivider: {
    fontSize: '20px',
    color: '#cbd5e1',
    fontWeight: '600',
  },
  totalQuestions: {
    fontSize: '18px',
    color: '#64748b',
    fontWeight: '600',
  },
  topicContainer: {
    marginBottom: '16px',
    background: '#f8fafc',
    padding: '8px 12px',
    borderRadius: '10px',
    border: '1px solid #f1f5f9'
  },
  topicValue: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
  },
  attemptsContainer: {
    marginBottom: '12px',
  },
  attemptsText: {
    fontSize: '13px',
    color: '#64748b',
    fontWeight: '500',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
  },
  periodBadge: {
    backgroundColor: '#eff6ff',
    color: '#3b82f6',
    fontSize: '12px',
    fontWeight: '700',
    padding: '4px 14px',
    borderRadius: '20px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#94a3b8',
    background: '#f8fafc',
    borderRadius: '20px',
  },
  emptyText: {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: '#64748b',
    margin: '0 0 8px 0',
  },
  emptySubtext: {
    fontSize: '14px',
    margin: '0',
    color: '#94a3b8',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #edf2f7',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  loadingText: {
    fontSize: '16px',
    color: '#64748b',
    fontWeight: '600',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    padding: '20px',
    textAlign: 'center',
  },
  errorText: {
    color: '#ef4444',
    fontSize: '1.1rem',
    fontWeight: '700',
    margin: '0 0 8px 0',
  },
  errorSubtext: {
    color: '#64748b',
    fontSize: '14px',
    margin: '0',
  },
};

// Add the animation to the document
if (typeof document !== 'undefined') {
  const styleSheet = document.styleSheets[0];
  styleSheet.insertRule(`
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `, styleSheet.cssRules.length);
}

// Media queries for responsiveness
const mediaQueries = `
  @media (max-width: 768px) {
    .container {
      padding: 12px;
      margin-top: 80px;
    }
    
    .header {
      margin-bottom: 24px;
    }
    
    .section {
      padding: 24px 16px;
      margin-bottom: 20px;
    }
    
    .list {
      grid-template-columns: 1fr;
      gap: 16px;
    }
    
    .card {
      padding: 16px;
    }
    
    .tab {
      padding: 8px 18px;
      font-size: 13px;
      min-width: 80px;
    }
    
    .section-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
  }
  
  @media (max-width: 480px) {
    .tab-container {
      flex-direction: column;
      align-items: stretch;
    }
    
    .tab {
      width: 100%;
      margin-bottom: 8px;
    }
  }
`;

// Add media queries to the document
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = mediaQueries;
  document.head.appendChild(style);
}

export default ToppersWall;